import { useQuery } from '@tanstack/react-query';
import { useOkapiKy } from '@folio/stripes/core';

const isEmpty = (query) =>
  !query ||
  query === '' ||
  (typeof query === 'object' && Object.keys(query).length === 0);

/**
 * Converts the raw JSON response from /report-execution into a
 * ResultSet-compatible object so existing display components work unchanged.
 */
const buildResultSet = (responseData) => {
  // Backend now returns: { status, totalRecords, data (array), annotation, query }
  // Support both new flat structure and old map-wrapped structure
  const data = responseData?.data ||
    responseData?.map?.data?.list ||
    [];
  const total = responseData?.totalRecords ??
    responseData?.map?.totalRecords ??
    data.length;
  const annotation = responseData?.annotation ||
    responseData?.map?.annotation ||
    {};

  // Build seriesNames and chartPivot from annotation for chart views
  const measures = Object.keys(annotation?.measures || {});
  const dimensions = Object.keys(annotation?.dimensions || {});

  const seriesNames = () => measures.map(key => ({
    key: key.replace('.', '_'),
    title: annotation.measures[key]?.shortTitle || key,
    shortTitle: annotation.measures[key]?.shortTitle || key,
    yValues: [key.replace('.', '_')],
  }));

  const chartPivot = () => data.map(row => {
    const x = dimensions.map(d => row[d]).join(', ') || '';
    const point = { x, xValues: dimensions.map(d => row[d]) };
    measures.forEach(m => { point[m.replace('.', '_')] = parseFloat(row[m]) || 0; });
    return point;
  });

  return {
    loadResponses: [{ data, total, dimensions, annotation }],
    rawData: () => data,
    tablePivot: () => data,
    chartPivot,
    series: () => [],
    seriesNames,
    totalRow: () => ({}),
    annotation: () => annotation,
  };
};

export default function useCubeQuery(query, options = {}) {
  const okapiKy = useOkapiKy();

  const {
    data: resultSet,
    error,
    isFetching,
  } = useQuery({
    queryKey: ['cube-query', JSON.stringify(query)],
    queryFn: async () => {
      if (isEmpty(query)) return null;

      // Remove non-query fields before sending to backend
      const { view, fieldAliasMap, ...cubeQuery } = typeof query === 'object' ? query : {};

      const response = await okapiKy
        .post('report-execution', { json: { cubeQuery } })
        .json();

      return buildResultSet(response);
    },
    enabled: !isEmpty(query),
    staleTime: 2 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
    retry: 1,
    retryDelay: 2000,
    onSuccess: options.onSuccess,
  });

  return [resultSet, error, isFetching];
}
